import React, { useState, useEffect } from 'react'
import ClientView from './ClientView'
import { useFaceProfile } from '../../../utils/useFaceProfile'
import DOMPurify from 'dompurify'
import axios from 'axios'
import { APIENDPOINT, getCWPEnvironmentForURL } from '../../../utils/common-utils'
import { isValidUrl } from '@ampdx/common-utils/src'

/** ======================
 * Types
 * ====================== */

type AdvisorType = 'advisor' | 'team'

interface AdvisorUrls {
  referme?: string
  site?: string
}

interface AdvisorSocial {
  linkedin?: string
  [key: string]: string | undefined
}

export interface AdvisorData {
  type: AdvisorType
  email?: string
  phone?: string
  name?: string
  image?: string
  designations?: string
  title?: string
  urls: AdvisorUrls
  social: AdvisorSocial
}

interface FaceProfileHook {
  advisorData?: AdvisorData | null
}

interface ContentLabels {
  image?: string
  body?: string
  // other label fields are allowed but not required here
  [key: string]: unknown
}

export interface AdvisorProps {
  content: ContentLabels
}

/** ======================
 * Helpers
 * ====================== */

const adjustAdvisorData = (data: any): AdvisorData => {
  const DACData: AdvisorData = {
    type: 'advisor',
    email: data.email,
    phone: data.phonenumber,
    name: data.displayname,
    image: data.profilesmallimage || data.profilelargeimage,
    designations: data.professionaldesignations,
    title: data.proffessionaltitle,
    urls: {
      referme: data.referMeUrl,
      site: data.siteUrl,
    },
    social: {
      linkedin: data.linkedInUrl,
    },
  }

  return DACData
}

/** ======================
 * Hook
 * ====================== */

export const useAdvisorInfo = () => {
  const [advisorData, setAdvisorData] = useState<AdvisorData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const getAdvisorIdFromQuery = () => {
      const query = new URLSearchParams(window.location.search)
      return query.get('advisorid') || query.get('advisorId')
    }

    const getLocalAdvisorResults = (advisorIdParam: string | null) => {
      const localStorageResults = localStorage.getItem('results')
      if (!localStorageResults || localStorageResults === '{}') return null
      try {
        const parsed = JSON.parse(localStorageResults)
        return parsed.email === advisorIdParam ? parsed : null
      } catch {
        return null
      }
    }

    const fetchAdvisorFromApi = async (advisorInfoUrl: string) => {
      const response = await axios.get(isValidUrl(advisorInfoUrl))
      if (response.data.status !== '1') {
        throw new Error(response.data.error?.description || 'Unknown error')
      }
      localStorage.setItem('results', JSON.stringify(response.data.result))
      return response.data.result
    }

    const fetchAdvisorData = async (advisorIdParam: string | null) => {
      if (!advisorIdParam) return
      const env = getCWPEnvironmentForURL(window.location.href)
      const advisorInfoUrl = `${APIENDPOINT(env).advisor}GetAdvisorById?advisorId=${advisorIdParam}`

      let results = getLocalAdvisorResults(advisorIdParam)
      if (!results) {
        try {
          results = await fetchAdvisorFromApi(advisorInfoUrl)
        } catch (err: any) {
          setError(err?.message || 'Error fetching advisor data')
          return
        }
      }
      setAdvisorData(adjustAdvisorData(results))
    }

    const advisorIdParam = getAdvisorIdFromQuery()
    if (advisorIdParam) fetchAdvisorData(advisorIdParam)
  }, [])

  return { advisorData, error }
}

/** ======================
 * Component
 * ====================== */

const Advisor = (props: AdvisorProps) => {
  const { advisorData: data1 } = useFaceProfile()
  const { advisorData: data2 } = useAdvisorInfo()
  const advisorData = data1 || data2

  if (!advisorData) return null

  return (
    <div
      className='Advisor Advisor--color u-bgSizeCover'
      style={{
        backgroundImage: `url(${props.content?.image ?? ''})`,
      }}
    >
      <div className='u-sizeConstrained u-paddingVert32'>
        <div className='Card'>
          <ClientView data={advisorData} labels={props.content} />
        </div>
        <div
          className='AdvisorClient-disclosure AdvisorClient-disclosure--nextGen2024 Content--reversed u-textSizeMsrm2 u-marginTop8'
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props?.content?.body as string) }}
        />
      </div>
    </div>
  )
}

export default Advisor
